import { readCampaignParams } from "./campaign-params";

type DataLayerEvent = {
    event: string;
    [key: string]: unknown;
};

declare global {
    interface Window {
        dataLayer: DataLayerEvent[];
    }
}

const isBrowser = typeof window !== "undefined";

/**
 * Die Kampagnenfelder der aktuellen Adresse, geprueft.
 *
 * Leer, wo keine Kampagne anliegt — das ist der Normalfall, und ein leeres Feld
 * waere in GA4 nicht von einem gesetzten zu unterscheiden, das man nur nicht
 * ausliest (dieselbe Begruendung wie bei `trackJourneyStep`).
 */
const campaignFields = (): Record<string, string> => {
    if (!isBrowser) {
        return {};
    }
    const { campaign, campaignContent } = readCampaignParams(window.location.search);
    return {
        ...(campaign ? { campaign } : {}),
        ...(campaignContent ? { campaign_content: campaignContent } : {}),
    };
};

export const pushTagEvent = (event: DataLayerEvent): void => {
    if (!isBrowser) {
        return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
};

type TrackPageViewInput = {
    path: string;
    title?: string;
    language?: string;
};

export const trackPageView = ({ path, title, language }: TrackPageViewInput): void => {
    pushTagEvent({
        event: "page_view",
        page_path: path,
        page_title: title,
        page_language: language,
    });
};

type TrackCtaClickInput = {
    label?: string;
    href?: string;
    urlType?: string;
    pagePath?: string;
};

export const trackCtaClick = ({ label, href, urlType, pagePath }: TrackCtaClickInput): void => {
    pushTagEvent({
        event: "cta_click",
        cta_label: label,
        cta_href: href,
        cta_url_type: urlType,
        page_path: pagePath,
    });
};

type TrackFormStepInput = {
    formName: string;
    step: number;
    action: "next" | "back" | "submit_success" | "submit_error";
};

export const trackFormStep = ({ formName, step, action }: TrackFormStepInput): void => {
    pushTagEvent({
        event: "form_step",
        form_name: formName,
        form_step: step,
        form_action: action,
    });
};

type TrackConversionInput = {
    conversionName: string;
    conversionCategory?: string;
    pagePath?: string;
    href?: string;
    /**
     * Weitere Felder, die unveraendert ins `conversion_event` gehen, z.B.
     * `campaign` oder `campaign_page`.
     *
     * Ein eigenes Feld statt eines Spreads in die Eingabe: Bis 15.10.2026
     * reichte `trackLeadConversion` die Kampagnenfelder per Spread hinein, und
     * `trackConversion` verwarf sie, weil es nur seine vier benannten Felder
     * schrieb. Der Typecheck sah das nicht (QUAWEB-42).
     */
    fields?: Record<string, string>;
};

export const trackConversion = ({
    conversionName,
    conversionCategory = "click",
    pagePath,
    href,
    fields = {},
}: TrackConversionInput): void => {
    pushTagEvent({
        event: "conversion_event",
        conversion_name: conversionName,
        conversion_category: conversionCategory,
        page_path: pagePath,
        conversion_href: href,
        ...fields,
    });
};

/**
 * Die sechs Formulare, aus denen ein Lead entsteht (20.8.2026).
 *
 * `primary` heisst: jemand will mit uns sprechen. `secondary`: jemand will
 * etwas lesen und gibt dafür seine Adresse. Nur `primary` zählt als
 * Kontaktaufnahme im Sinne des SEO-Ziels; `secondary` wird getrennt
 * ausgewiesen und nicht dazugezählt.
 *
 * Bewusst eine Konstante statt freier Zeichenketten: Die vier Formulare, die
 * vorher meldeten, taten es unter je einem eigenen Namen, aus dem GA4 keine
 * gemeinsame Zielgrösse bilden konnte.
 */
export const LEAD_CONVERSIONS = {
    contact_request: "primary",
    workshop_inquiry: "primary",
    prototype_request: "primary",
    whitepaper_download: "secondary",
    refcard_download: "secondary",
    survey_signup: "secondary",
} as const satisfies Record<string, "primary" | "secondary">;

export type LeadConversion = keyof typeof LEAD_CONVERSIONS;

type TrackLeadConversionOptions = {
    /**
     * Kennung der Kampagnenseite, auf der die Anfrage entstand, z.B.
     * `ausschreibungen`. Geht als `campaign_page` ins Event.
     *
     * Nicht `campaign`: Dieser Name traegt bereits `utm_campaign`. Eine
     * GA4-Dimension mit zwei Bedeutungen liesse sich nicht mehr auswerten.
     */
    campaignPage?: string;
};

/**
 * Ein abgeschicktes Lead-Formular.
 *
 * Der Seitenpfad wird hier abgeleitet und nicht als Parameter genommen, damit
 * er an keiner Aufrufstelle vergessen werden kann. Ohne ihn lässt sich die
 * Kette Suchbegriff, Landingpage, Abschluss nicht schliessen.
 *
 * Nur im Erfolgszweig aufrufen: Ein Formular, dessen Server-Aufruf
 * fehlschlägt, ist keine Conversion.
 */
export const trackLeadConversion = (name: LeadConversion, { campaignPage }: TrackLeadConversionOptions = {}): void => {
    if (!isBrowser) {
        return;
    }
    trackConversion({
        conversionName: name,
        conversionCategory: LEAD_CONVERSIONS[name],
        pagePath: `${window.location.pathname}${window.location.search}`,
        fields: {
            /* Die Kampagne wird hier abgeleitet und nicht als Parameter
               genommen — aus demselben Grund wie der Seitenpfad darueber: damit
               sie an keiner Aufrufstelle vergessen werden kann. `campaignFields`
               gibt ein leeres Objekt zurueck, wo keine Kampagne anliegt. */
            ...campaignFields(),
            // Weggelassen statt leer, aus demselben Grund wie bei `campaignFields`.
            ...(campaignPage ? { campaign_page: campaignPage } : {}),
        },
    });
};

/**
 * Die vier Reisen, die ein Besucher durch das Angebot nimmt. Sie beschreiben
 * seine Lage, nicht unser Sortiment — «mieten» ist die Reise, die auf einer
 * KI-Anwendung endet, «bauen» die, die bei System Engineering ankommt.
 */
export const JOURNEYS = ["erproben", "mieten", "bauen", "einsteigen"] as const;

export type Journey = (typeof JOURNEYS)[number];

/**
 * Prüft einen Wert aus dem Content gegen die vier Reisen (7.8.2026).
 *
 * Velite leitet aus MDX nur `string` ab — ein Tippfehler im `journey`-Feld
 * einer Wegweiser-Karte würde sonst als eigene Reise im Reporting auftauchen
 * und dort erst Monate später auffallen. Lieber kein Event als ein falsches.
 */
export const isJourney = (value: string | undefined): value is Journey =>
    value !== undefined && (JOURNEYS as readonly string[]).includes(value);

type TrackJourneyStepInput = {
    journey: Journey;
    /** 1 Einstieg · 2 Detailseite · 3 Formular geöffnet · 4 abgeschickt */
    step: 1 | 2 | 3 | 4;
    /** Trennt die Offertenanfrage (primär) von der Terminanfrage (sekundär). */
    conversionType?: "offer" | "appointment";
    /** Woher der Einstieg kam, z.B. "wegweiser" für die Startseiten-Karten. */
    entry?: string;
};

/**
 * Ein Schritt auf einer der vier Reisen.
 *
 * Bewusst ein benannter Helfer statt `trackCustomEvent`: Letzterer lässt jedes
 * Feld durch, weshalb die 26 bestehenden Events ohne gemeinsame Struktur
 * entstanden sind. Ein Helfer macht das Schema zur Vorgabe.
 *
 * Nicht belegte Felder werden weggelassen statt als `undefined` gesendet — in
 * GTM wäre ein leeres Feld sonst nicht von einem gesetzten zu unterscheiden,
 * das man nur nicht ausliest.
 */
export const trackJourneyStep = ({ journey, step, conversionType, entry }: TrackJourneyStepInput): void => {
    if (!isBrowser) {
        return;
    }
    pushTagEvent({
        event: "journey_step",
        journey,
        journey_step: step,
        page_path: `${window.location.pathname}${window.location.search}`,
        ...(conversionType ? { conversion_type: conversionType } : {}),
        ...(entry ? { journey_entry: entry } : {}),
    });
};

/**
 * Die Mat-Variante im Feld `mat_variant`: immer `3d`, jeder Besuch bekommt die
 * 3D-Figur (Entscheid Jan, 10.10.2026). Das Feld bleibt, weil GTM und GA4 es lesen.
 */
export type MatVariant = "3d";

/** Warum ein Besuch der Variante `3d` die SVG-Figur sieht. */
export const MAT_FALLBACK_REASONS = [
    "no_webgl",
    "context_lost",
    "shader",
    "watchdog",
    "slow_frames",
    "save_data",
    "reduced_motion",
    "chunk_load",
] as const;

export type MatFallbackReason = (typeof MAT_FALLBACK_REASONS)[number];

type TrackMatVariantInput =
    | { event: "mat_exposure"; variant: MatVariant }
    | { event: "mat_fallback"; variant: MatVariant; reason: MatFallbackReason; healCount: number };

/**
 * Wo die Mat-Figur steht. Die Startseite sendet `mat_exposure`/`mat_fallback`
 * (A/B-Test, Plan `2026-10-02-mat-3d-website`), die KI-Technologie-Seite
 * dieselben Events mit dem Praefix `ki_` (Plan `2026-10-10-mat-3d-ki-technologie`).
 * Der Startseiten-Test liest nur die Namen ohne Praefix.
 */
export const MAT_EVENT_CONTEXTS = ["home", "ki"] as const;

export type MatEventContext = (typeof MAT_EVENT_CONTEXTS)[number];

const MAT_EVENT_PREFIX = { home: "", ki: "ki_" } as const satisfies Record<MatEventContext, string>;

type MatEventName = `${(typeof MAT_EVENT_PREFIX)[MatEventContext]}${TrackMatVariantInput["event"]}`;

const matEventName = (event: TrackMatVariantInput["event"], context: MatEventContext): MatEventName =>
    `${MAT_EVENT_PREFIX[context]}${event}`;

/**
 * Welche Mat-Figur ein Besuch sah, und warum er auf die SVG zurueckfiel.
 *
 * `mat_exposure` einmal pro Seitenaufruf, `mat_fallback` beim Rueckfall auf
 * die SVG. Mit `context: "ki"` heissen die Events `ki_mat_exposure` und
 * `ki_mat_fallback`; die Felder bleiben gleich. Ein benannter Helfer statt
 * `trackCustomEvent`, aus demselben Grund wie bei `trackJourneyStep`.
 */
export const trackMatVariant = (input: TrackMatVariantInput, context: MatEventContext = "home"): void => {
    if (!isBrowser) {
        return;
    }
    if (input.event === "mat_exposure") {
        pushTagEvent({
            event: matEventName(input.event, context),
            mat_variant: input.variant,
            page_path: `${window.location.pathname}${window.location.search}`,
        });
    } else {
        pushTagEvent({
            event: matEventName(input.event, context),
            mat_variant: input.variant,
            reason: input.reason,
            heal_count: input.healCount,
        });
    }
};

/**
 * Push an arbitrary custom event with payload onto the dataLayer. Use for
 * page-specific interactions that don't fit the generic CTA/form/conversion
 * helpers above — quiz progress, modal opens, tier-specific CTAs, etc.
 */
export const trackCustomEvent = (eventName: string, payload: Record<string, unknown> = {}): void => {
    if (!isBrowser) {
        return;
    }
    try {
        pushTagEvent({
            event: eventName,
            page_path: `${window.location.pathname}${window.location.search}`,
            ...payload,
        });
    } catch {
        /* dataLayer push may throw if GTM hijacks it — fail silently */
    }
};
